import React, { useState, useEffect } from 'react';
import { Card, Table, Spin, DatePicker, Button, Row, Col, Input, Checkbox, Space, Tabs, Tag, notification, Modal, Radio, Tooltip } from 'antd';
import { EyeOutlined, DownloadOutlined, FileExcelOutlined, EditOutlined } from "@ant-design/icons";
import { httpGet, httpGetBlob, customBaseUrl } from '../../store/http';
import { NotificationManager } from 'react-notifications';
import { currencyFormat, formatDate } from '../../utils/helper';
import moment from 'moment';
import FundGLModal from './modal/FundGLModal';
import CreateGLAccountModal from './modal/CreateGLAccountModal';
import EditGLAccountModal from './modal/EditGLAccountModal';
import BulkDisbursementModal from './modal/BulkDisbursementModal';
import BulkBatchDetailsModal from './modal/BulkBatchDetailsModal';

const { TabPane } = Tabs;

const GLAccounts = () => {
    const [allAccounts, setAllAccounts] = useState([]);
    const [transactions, setTransactions] = useState([]);
    const [bulkHistory, setBulkHistory] = useState([]);
    const [loading, setLoading] = useState({ accounts: false, transactions: false, history: false });
    const [dateRange, setDateRange] = useState([moment().subtract(30, 'days'), moment()]);
    const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
    const [selectedAccount, setSelectedAccount] = useState(null);
    const [searchAccountCode, setSearchAccountCode] = useState('');
    const [overviewSearchText, setOverviewSearchText] = useState('');
    const [useDateRangeForAccount, setUseDateRangeForAccount] = useState(true);
    const [filterAccountsByDate, setFilterAccountsByDate] = useState(false);
    const [showFundModal, setShowFundModal] = useState(false);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [selectedAccountForEdit, setSelectedAccountForEdit] = useState(null);
    const [selectedAccountForFunding, setSelectedAccountForFunding] = useState(null);
    const [selectedAccountForBulk, setSelectedAccountForBulk] = useState(null);
    const [showBulkModal, setShowBulkModal] = useState(false);
    const [showBatchDetails, setShowBatchDetails] = useState(false);
    const [selectedBatchRef, setSelectedBatchRef] = useState(null);
    const [viewMode, setViewMode] = useState('list'); // 'list' or 'detail'
    const [activeMainTab, setActiveMainTab] = useState('accounts');
    const [showExportModal, setShowExportModal] = useState(false);
    const [exportFormat, setExportFormat] = useState('xlsx');
    const [exportAccountCode, setExportAccountCode] = useState(null);
    const [exporting, setExporting] = useState(false);
    const [exportDateRange, setExportDateRange] = useState([moment().subtract(1, 'month'), moment()]);

    const exportGLEntries = async (accountCode, format, startDate, endDate) => {
        setExporting(true);
        try {
            let url = `${customBaseUrl.tranv2}/api/v1/admin/gl-statistics/export/by-gl-code/${accountCode}?format=${format}`;
            if (startDate && endDate) {
                url += `&startDate=${startDate.format("YYYY-MM-DDTHH:mm:ss")}`;
                url += `&endDate=${endDate.format("YYYY-MM-DDTHH:mm:ss")}`;
            }
            const blob = await httpGetBlob(url);
            if (blob) {
                const fileExtension = format === 'csv' ? 'csv' : 'xlsx';
                const fileName = `GL_${accountCode}_entries.${fileExtension}`;
                const downloadUrl = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = downloadUrl;
                link.setAttribute('download', fileName);
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(downloadUrl);
                NotificationManager.success(`GL entries exported as ${format.toUpperCase()} successfully.`);
                setShowExportModal(false);
            } else {
                NotificationManager.error('Failed to download the export file.');
            }
        } catch (error) {
            NotificationManager.error('Error exporting GL entries.');
            console.error('Export error:', error);
        } finally {
            setExporting(false);
        }
    };

    const fetchAllAccounts = async () => {
        setLoading(prev => ({ ...prev, accounts: true }));
        try {
            let url = `${customBaseUrl.tranv2}/api/v1/admin/gl-statistics/account/all`;
            if (filterAccountsByDate) {
                const [startDate, endDate] = dateRange;
                if (startDate && endDate) {
                    const params = {
                        startDate: startDate.format('YYYY-MM-DD'),
                        endDate: endDate.format('YYYY-MM-DD'),
                    };
                    const queryString = new URLSearchParams(params).toString();
                    url = `${customBaseUrl.tranv2}/api/v1/admin/gl-statistics/daily-summary/date-range?${queryString}`;
                }
            }
            const res = await httpGet(url);
            if (res.success && Array.isArray(res.data)) {
                setAllAccounts(res.data);
            } else {
                setAllAccounts([]);
                NotificationManager.error(res.message || 'Failed to fetch all accounts.');
            }
        } catch (error) {
            setAllAccounts([]);
            NotificationManager.error('Error fetching all GL accounts.');
        } finally {
            setLoading(prev => ({ ...prev, accounts: false }));
        }
    };

    const fetchTransactions = async (page = 1, size = 10, dates = dateRange, account = selectedAccount) => {
        setLoading(prev => ({ ...prev, transactions: true }));

        let url;
        let params = {
            page: page - 1,
            size: size,
        };

        if (account) {
            if (useDateRangeForAccount) {
                const [startDate, endDate] = dates;
                params.startDate = startDate.format('YYYY-MM-DD');
                params.endDate = endDate.format('YYYY-MM-DD');
                const queryString = new URLSearchParams(params).toString();
                url = `${customBaseUrl.tranv2}/api/v1/admin/gl-statistics/transactions/account/${account.accountCode}/date-range?${queryString}`;
            } else {
                const queryString = new URLSearchParams(params).toString();
                url = `${customBaseUrl.tranv2}/api/v1/admin/gl-statistics/transactions/account/${account.accountCode}?${queryString}`;
            }
        } else {
            const [startDate, endDate] = dates;
            params.startDate = startDate.format('YYYY-MM-DD');
            params.endDate = endDate.format('YYYY-MM-DD');
            const queryString = new URLSearchParams(params).toString();
            url = `${customBaseUrl.tranv2}/api/v1/admin/gl-statistics/transactions/all?${queryString}`;
        }

        try {
            const res = await httpGet(url);
            if (res.success) {
                const content = res.data?.content || (Array.isArray(res.data) ? res.data : []);
                const total = res.data?.totalElements || content.length;
                setTransactions(content);
                setPagination(prev => ({ ...prev, total: total, current: page }));
            } else {
                NotificationManager.error(res.message || 'Failed to fetch transactions.');
            }
        } catch (error) {
            NotificationManager.error('Error fetching GL transactions.');
        } finally {
            setLoading(prev => ({ ...prev, transactions: false }));
        }
    };

    const fetchBulkHistory = async (page = 0, size = 15) => {
        setLoading(prev => ({ ...prev, history: true }));
        try {
            const url = `${customBaseUrl.tranv2}/api/v1/admin/bulk-disbursement?page=${page}&size=${size}`;
            const res = await httpGet(url);
            if (res.success) {
                setBulkHistory(res.data.content || []);
            } else {
                notification.error({ message: res.message || "Failed to fetch bulk history." });
            }
        } catch (error) {
            notification.error({ message: error.message || "An error occurred while fetching history." });
        } finally {
            setLoading(prev => ({ ...prev, history: false }));
        }
    };

    useEffect(() => {
        fetchAllAccounts();
        fetchBulkHistory();
    }, []);

    useEffect(() => {
        if (viewMode === 'detail' && selectedAccount) {
            fetchTransactions(1, pagination.pageSize, dateRange, selectedAccount);
        }
    }, [selectedAccount, viewMode, useDateRangeForAccount]);

    const handleTableChange = (pagination) => {
        fetchTransactions(pagination.current, pagination.pageSize, dateRange, selectedAccount);
    };

    const handleFilter = () => {
        if (activeMainTab === 'accounts' && viewMode === 'detail') {
            fetchTransactions(1, pagination.pageSize, dateRange, selectedAccount);
        } else if (activeMainTab === 'history') {
            fetchBulkHistory();
        }
        fetchAllAccounts();
    };

    const handleAccountCodeSearch = () => {
        if (searchAccountCode.trim()) {
            const foundAccount = allAccounts.find(a => a.accountCode === searchAccountCode.trim());
            const accountToSet = foundAccount || { accountCode: searchAccountCode.trim(), accountName: `Account ${searchAccountCode.trim()}` };
            setSelectedAccount(accountToSet);
            setViewMode('detail');
        }
    };

    const glAccountColumns = [
        { title: 'Account Name', dataIndex: 'accountName', key: 'accountName', fontWeight: 'bold' },
        { title: 'Account Code', dataIndex: 'accountCode', key: 'accountCode' },
        { title: 'Current Balance', dataIndex: 'currentBalance', key: 'currentBalance', render: (val) => currencyFormat(val) },
        { title: 'Total Debits', dataIndex: 'totalDebits', key: 'totalDebits', render: (val) => currencyFormat(val) },
        { title: 'Total Credits', dataIndex: 'totalCredits', key: 'totalCredits', render: (val) => currencyFormat(val) },
        { title: 'Transactions', dataIndex: 'transactionCount', key: 'transactionCount' },
        {
            title: 'Action',
            key: 'action',
            render: (_, record) => (
                <Space size="middle">
                    <Button
                        size="small"
                        onClick={() => {
                            setSelectedAccount(record);
                            setViewMode('detail');
                        }}
                    >
                        View Transactions
                    </Button>
                    <Button
                        size="small"
                        type="primary"
                        style={{ background: '#FF4B01', border: 'none' }}
                        onClick={() => {
                            setSelectedAccountForFunding(record);
                            setShowFundModal(true);
                        }}
                    >
                        Fund
                    </Button>
                    <Tooltip title="Edit GL account">
                        <Button
                            size="small"
                            icon={<EditOutlined />}
                            onClick={() => {
                                setSelectedAccountForEdit(record);
                                setShowEditModal(true);
                            }}
                        >
                            Edit
                        </Button>
                    </Tooltip>
                    <Tooltip title="Export GL entries">
                        <Button
                            size="small"
                            icon={<DownloadOutlined />}
                            onClick={() => {
                                setExportAccountCode(record.accountCode);
                                setShowExportModal(true);
                            }}
                        >
                            Export
                        </Button>
                    </Tooltip>
                </Space>
            ),
        },
    ];

    const transactionColumns = [
        { title: 'Date', dataIndex: 'entryDate', key: 'entryDate', render: (text) => moment(text).format('YYYY-MM-DD HH:mm') },
        { title: 'Description', dataIndex: 'description', key: 'description' },
        { title: 'GL Account', dataIndex: 'glAccountName', key: 'glAccountName', render: (text, record) => `${text} (${record.glAccountCode})` },
        { title: 'Customer Account', dataIndex: 'customerAccountNumber', key: 'customerAccountNumber' },
        { title: 'Debit', dataIndex: 'debitAmount', key: 'debitAmount', render: (val) => currencyFormat(val) },
        { title: 'Credit', dataIndex: 'creditAmount', key: 'creditAmount', render: (val) => currencyFormat(val) },
        { title: 'Reference', dataIndex: 'reference', key: 'reference' },
        { title: 'Entry Type', dataIndex: 'entryType', key: 'entryType' },
    ];

    const bulkHistoryColumns = [
        { title: 'Date', dataIndex: 'createdAt', key: 'createdAt', render: (val) => formatDate(val) },
        { title: 'Batch Reference', dataIndex: 'batchReference', key: 'batchReference' },
        { title: 'GL Account', dataIndex: 'glAccountCode', key: 'glAccountCode' },
        { title: 'Narration', dataIndex: 'narration', key: 'narration' },
        { title: 'Total Amount', dataIndex: 'totalAmount', key: 'totalAmount', render: (val) => currencyFormat(val) },
        { title: 'Items', dataIndex: 'totalItems', key: 'totalItems' },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (status) => (
                <Tag color={status === 'COMPLETED' ? 'green' : 'blue'}>{status}</Tag>
            )
        },
        {
            title: 'Action',
            key: 'action',
            render: (_, record) => (
                <Button
                    size="small"
                    icon={<EyeOutlined />}
                    onClick={() => {
                        setSelectedBatchRef(record.batchReference);
                        setShowBatchDetails(true);
                    }}
                >
                    View Details
                </Button>
            ),
        },
    ];

    const filteredAccounts = overviewSearchText.trim()
        ? allAccounts.filter((a) => {
            const q = overviewSearchText.trim().toLowerCase();
            return (
                (a.accountName || '').toLowerCase().includes(q) ||
                (a.accountCode || '').toLowerCase().includes(q)
            );
        })
        : allAccounts;

    const renderAccountsView = () => (
        viewMode === 'list' ? (
            <Card
                title="GL Accounts Overview"
                extra={
                    <Space>
                        <Input.Search
                            placeholder="Search by name or code"
                            value={overviewSearchText}
                            onChange={(e) => setOverviewSearchText(e.target.value)}
                            allowClear
                            style={{ width: 240 }}
                        />
                        <Button
                            onClick={() => setShowCreateModal(true)}
                            style={{ borderRadius: 4 }}
                        >
                            Create GL Account
                        </Button>
                        <Button
                            type="primary"
                            onClick={() => {
                                setSelectedAccountForFunding(null);
                                setShowFundModal(true);
                            }}
                            style={{ background: '#FF4B01', border: 'none', borderRadius: 4 }}
                        >
                            Fund GL Account
                        </Button>
                        <Button
                            type="primary"
                            onClick={() => {
                                setSelectedAccountForBulk(null);
                                setShowBulkModal(true);
                            }}
                            style={{ background: '#0050b3', border: 'none', borderRadius: 4 }}
                        >
                            Bulk Disbursement
                        </Button>
                    </Space>
                }
            >
                <Table
                    dataSource={filteredAccounts}
                    columns={glAccountColumns}
                    rowKey="accountCode"
                    loading={loading.accounts}
                    pagination={{ pageSize: 15 }}
                />
            </Card>
        ) : (
            <Card
                title={
                    <Space>
                        <span>Transactions for: <strong>{selectedAccount?.accountName}</strong></span>
                        <span style={{ color: '#888', fontSize: 'small' }}>({selectedAccount?.accountCode})</span>
                    </Space>
                }
                extra={
                    <Space>
                        <Checkbox
                            checked={useDateRangeForAccount}
                            onChange={(e) => setUseDateRangeForAccount(e.target.checked)}
                        >
                            Use Date Range
                        </Checkbox>
                        <Button
                            icon={<DownloadOutlined />}
                            onClick={() => {
                                setExportAccountCode(selectedAccount?.accountCode);
                                setShowExportModal(true);
                            }}
                        >
                            Export Entries
                        </Button>
                    </Space>
                }
            >
                <Table
                    dataSource={transactions}
                    columns={transactionColumns}
                    rowKey="id"
                    loading={loading.transactions}
                    pagination={pagination}
                    onChange={handleTableChange}
                />
            </Card>
        )
    );

    return (
        <div>
            <Card style={{ marginBottom: 24 }}>
                <Row gutter={[16, 16]} align="middle" justify="space-between">
                    <Col>
                        <Space size="large">
                            <DatePicker.RangePicker
                                value={dateRange}
                                onChange={(dates) => setDateRange(dates)}
                            />
                            {activeMainTab === 'accounts' && viewMode === 'list' && (
                                <Checkbox
                                    checked={filterAccountsByDate}
                                    onChange={(e) => setFilterAccountsByDate(e.target.checked)}
                                >
                                    Filter overview by date
                                </Checkbox>
                            )}
                            <Button type="primary" onClick={handleFilter}>Filter</Button>
                            {activeMainTab === 'accounts' && viewMode === 'detail' && (
                                <Button onClick={() => setViewMode('list')}>Back to List</Button>
                            )}
                        </Space>
                    </Col>
                    {activeMainTab === 'accounts' && viewMode === 'list' && (
                        <Col>
                            <Space>
                                <Input
                                    placeholder="Search by Account Code"
                                    value={searchAccountCode}
                                    onChange={(e) => setSearchAccountCode(e.target.value)}
                                    onPressEnter={handleAccountCodeSearch}
                                    style={{ width: 220 }}
                                />
                                <Button onClick={handleAccountCodeSearch}>Search Account</Button>
                            </Space>
                        </Col>
                    )}
                </Row>
            </Card>

            <Tabs activeKey={activeMainTab} onChange={setActiveMainTab}>
                <TabPane tab="Accounts Overview" key="accounts">
                    {renderAccountsView()}
                </TabPane>
                <TabPane tab="Bulk Transactions History" key="history">
                    <Card title="Bulk Disbursement History">
                        <Table
                            dataSource={bulkHistory}
                            columns={bulkHistoryColumns}
                            rowKey="batchReference"
                            loading={loading.history}
                            pagination={{ pageSize: 15 }}
                        />
                    </Card>
                </TabPane>
            </Tabs>

            <FundGLModal
                showModal={showFundModal}
                hideModal={() => setShowFundModal(false)}
                selectedAccount={selectedAccountForFunding}
                allAccounts={allAccounts}
                onSuccess={() => {
                    fetchAllAccounts();
                    if (viewMode === 'detail') fetchTransactions();
                }}
            />

            <CreateGLAccountModal
                showModal={showCreateModal}
                hideModal={() => setShowCreateModal(false)}
                onSuccess={() => {
                    fetchAllAccounts();
                }}
            />

            <EditGLAccountModal
                showModal={showEditModal}
                hideModal={() => {
                    setShowEditModal(false);
                    setSelectedAccountForEdit(null);
                }}
                account={selectedAccountForEdit}
                onSuccess={() => {
                    fetchAllAccounts();
                }}
            />

            <BulkDisbursementModal
                showModal={showBulkModal}
                hideModal={() => {
                    setShowBulkModal(false);
                    setSelectedBatchRef(null);
                }}
                selectedAccount={selectedAccountForBulk}
                allAccounts={allAccounts}
                initialBatchRef={selectedBatchRef}
                onSuccess={() => {
                    fetchAllAccounts();
                    fetchBulkHistory();
                    if (viewMode === 'detail') fetchTransactions();
                }}
            />

            <BulkBatchDetailsModal
                showModal={showBatchDetails}
                hideModal={() => setShowBatchDetails(false)}
                batchRef={selectedBatchRef}
                onContinue={(ref) => {
                    setSelectedBatchRef(ref);
                    setShowBulkModal(true);
                }}
            />

            {/* Export GL Entries Modal */}
            <Modal
                title={
                    <Space>
                        <FileExcelOutlined style={{ color: '#27AE60' }} />
                        <span>Export GL Entries</span>
                    </Space>
                }
                open={showExportModal}
                onCancel={() => {
                    setShowExportModal(false);
                    setExportAccountCode(null);
                }}
                footer={[
                    <Button
                        key="cancel"
                        onClick={() => {
                            setShowExportModal(false);
                            setExportAccountCode(null);
                        }}
                    >
                        Cancel
                    </Button>,
                    <Button
                        key="export"
                        type="primary"
                        icon={<DownloadOutlined />}
                        loading={exporting}
                        style={{ background: '#27AE60', border: 'none' }}
                        onClick={() => {
                            const [startDate, endDate] = exportDateRange || [];
                            exportGLEntries(exportAccountCode, exportFormat, startDate, endDate);
                        }}
                    >
                        Download
                    </Button>,
                ]}
            >
                <div style={{ padding: '16px 0' }}>
                    <p style={{ marginBottom: 8, fontWeight: 500 }}>Account Code:</p>
                    <Input value={exportAccountCode} disabled style={{ marginBottom: 16 }} />

                    <p style={{ marginBottom: 8, fontWeight: 500 }}>Export Format:</p>
                    <Radio.Group
                        value={exportFormat}
                        onChange={(e) => setExportFormat(e.target.value)}
                        style={{ marginBottom: 16 }}
                    >
                        <Radio.Button value="xlsx">XLSX (Excel)</Radio.Button>
                        <Radio.Button value="csv">CSV</Radio.Button>
                    </Radio.Group>

                    <p style={{ marginBottom: 8, fontWeight: 500 }}>Date Range (optional):</p>
                    <DatePicker.RangePicker
                        value={exportDateRange}
                        onChange={(dates) => setExportDateRange(dates)}
                        disabledDate={(current) => current && current > moment().endOf('day')}
                        style={{ width: '100%' }}
                    />
                </div>
            </Modal>
        </div>
    );
};

export default GLAccounts;
